import { Box, Icon, IconButton, LoaderNew, Spacer, SpacerNew, Text } from '@jfs/ui-new'
import { getLoadingState } from '@jfs/utils'
import { observer } from 'mobx-react'
import { FC, Fragment, useEffect } from 'react'
import { ScrollView } from 'react-native'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { useWindowDimensions } from '../../util/util'
import ConvenienceFeeDetails from './ConvenienceFeeDetails'
import CustomerDetails from './CustomerDetails'
import Details from './Details'
import Header from './Header'
import PaymentDetails from './PaymentDetails'
import RefundDetails from './RefundDetails'
import SplitSettlementDetails from './SplitSettlementDetails'
import { styles } from './styles'

export interface TransactionDetailsProps {
  transactionId?: string
  closeTrxDetailsView?: () => void
  visible?: boolean
  initiateRefund?: (id: string) => void
  path?: string
  transactionDate?: string
  essStatus?: string
  refundId?: string
}

export interface TitleValue {
  title: string
  value: string
}

const TransactionDetails: FC<TransactionDetailsProps> = ({
  visible,
  transactionId,
  closeTrxDetailsView,
  initiateRefund,
  path = Routes.TRANSACTIONS,
  transactionDate,
  refundId,
}: TransactionDetailsProps) => {
  const {
    newTransactionDetails,
    fetchTxnsDetails,
    canIssueRefund,
    canExportTransationsReport,
    isSplitSettlementsEnabled,
    getSplitStatusAndTransactions,
  } = useStores((store) => ({
    newTransactionDetails: store.transactions.newTransactionDetails,
    getSplitStatusAndTransactions: store.transactions.getSplitStatusAndTransactions,
    fetchTxnsDetails: store.fetchTxnsDetailsV3,
    canIssueRefund: store.userManagement.canIssueRefund(),
    canExportTransationsReport: store.userManagement.canExportTransationsReport(),
    isSplitSettlementsEnabled: store.settings.metaData.isSplitSettlementsEnabled,
  }))
  const [fetchDetails, detailsHandler] = useLazyApiData(fetchTxnsDetails)
  const [splitDataApi, splitDataApiHandler] = useLazyApiData(getSplitStatusAndTransactions)
  const isDetailsLoading = !detailsHandler.state || detailsHandler.state === 'pending'
  const isSplitLoading = !splitDataApiHandler.state || splitDataApiHandler.state === 'pending'
  const apis = [detailsHandler]
  const viewPortWidth = useWindowDimensions()

  const transactionDetailsObj = newTransactionDetails(path)
  const isAdjustment = transactionDetailsObj?.paymentDetails?.paymentType === 'Adjustment'
  const isRefunded = transactionDetailsObj?.isRefunded

  const themes = styles(viewPortWidth)
  useEffect(() => {
    if (transactionId !== '') {
      fetchDetails(path, transactionId, transactionDate)
    }
  }, [fetchDetails, path, transactionId, transactionDate])

  useEffect(() => {
    if (!isDetailsLoading) {
      splitDataApi(transactionId, transactionDate, path, refundId)
    }
  }, [isDetailsLoading, path, transactionId, transactionDate, splitDataApi, refundId])

  if (!visible || !transactionId) {
    return <></>
  }
  // onAction of dropdown is taking care of file download onChange is not used
  const handleOnChange = (value: string | number[]) => {
    console.log({ value })
  }
  const loadingState = getLoadingState(apis)

  const onRefund = () => {
    if (initiateRefund) {
      initiateRefund(transactionId)
    }
  }

  const hideRefunds = detailsHandler?.state === 'rejected' && detailsHandler?.data?.errSection !== 'split'
  const essStatus = transactionDetailsObj?.essStatus
  return (
    <ScrollView stickyHeaderIndices={[0]} style={themes.container} nativeID={'txndetails'}>
      <Header
        closeTrxDetailsView={closeTrxDetailsView}
        handleOnChange={handleOnChange}
        rrnValue={transactionId}
        ern={transactionDetailsObj?.ern}
        isRefunded={isRefunded}
        onRefund={onRefund}
        path={path}
        hide={loadingState}
        enableRefund={
          transactionDetailsObj?.details?.status === 'SUCCESS' && canIssueRefund && !isAdjustment && !isRefunded
        }
        txnItem={transactionDetailsObj}
        refundId={refundId}
        isLoading={isSplitLoading}
        showExport={canExportTransationsReport}
      />
      <Spacer size={20} hide={!loadingState} />
      <ModBox show={loadingState} f={1}>
        <LoaderNew color={'BLUE'} />
      </ModBox>
      {!loadingState && (
        <Fragment>
          <Details details={transactionDetailsObj?.details} path={path} />
          {/* <SettlementDetails settlementDetails={transactionDetailsObj?.settlementDetails} /> */}
          <PaymentDetails paymentDetails={transactionDetailsObj?.paymentDetails} />
          <CustomerDetails customer={transactionDetailsObj?.customerDetails} />
          <ConvenienceFeeDetails convenienceFeeDetails={transactionDetailsObj?.convenienceFee} />
          {!isAdjustment && (
            <>
              {!hideRefunds && <RefundDetails rrnValue={transactionId} />}
              {hideRefunds && transactionDetailsObj?.ern !== 'NA' && (
                <Box
                  onPress={() => fetchDetails(path, transactionId, transactionDate, refundId)}
                  testID="retry"
                  h={80}
                  align={'center'}
                  justify={'center'}
                  underlayColor={'TRANSPARENT'}>
                  <Icon id={'ICON_REFRESH'} size={20} />
                  <SpacerNew size={8} />
                  <Text
                    title={detailsHandler?.data?.message ?? 'Error Whle Processing'}
                    fontFamily={'jiotype-medium'}
                    fontSize={12}
                  />
                </Box>
              )}
            </>
          )}
          {isSplitSettlementsEnabled && essStatus === 'PAYOUT_SPLIT_INITIATED' && (
            <SplitSettlementDetails data={transactionDetailsObj?.splitTxns} />
          )}
          <ModBox
            align={'center'}
            justify={'center'}
            show={
              isSplitSettlementsEnabled &&
              essStatus === 'PAYOUT_SPLIT_INITIATED' &&
              (transactionDetailsObj?.splitTxns?.length === 0 || detailsHandler?.data?.errSection === 'split')
            }
            underlayColor={'TRANSPARENT'}>
            {detailsHandler?.data?.errSection === 'split' && (
              <IconButton
                id={'ICON_REFRESH'}
                size={20}
                testID="split"
                onPress={() => fetchDetails(path, transactionId, transactionDate, refundId)}
              />
            )}
            <SpacerNew size={8} />
            <Text
              title={detailsHandler?.data?.message ?? 'No records found'}
              fontFamily={'jiotype-medium'}
              fontSize={12}
            />
          </ModBox>
          <Spacer size={96} />
        </Fragment>
      )}
    </ScrollView>
  )
}

export default observer(TransactionDetails)
